"use client";

import Image from "next/image";
import { useState, useEffect } from "react";
import Loader from "../Loader";
import { MapPin, Store } from "lucide-react";
import styles from "./brandarea.module.css";

const Brands = ({ df, brands = [] }: { df?: boolean; brands?: any[] }) => {
  const [loading, setLoading]             = useState(false);
  const [displayBrands, setDisplayBrands] = useState<any[]>([]);
  const [status, setStatus]               = useState<"idle" | "loading" | "granted" | "denied" | "empty">("idle");

  const fetchNearbyBrands = async (lat: number, lng: number) => {
    setStatus("loading");
    try {
      const res  = await fetch(
        `https://i-mall-bo.vercel.app/api/v1/customer/brands/nearby?lat=${lat}&lng=${lng}&radius=3`,
        { cache: "no-store" }
      );
      const data = await res.json();
      if (data?.success && Array.isArray(data.data) && data.data.length > 0) {
        setDisplayBrands(data.data);
        setStatus("granted");
      } else {
        setDisplayBrands([]);
        setStatus("empty");
      }
    } catch {
      setDisplayBrands([]);
      setStatus("empty");
    }
  };

  const requestLocation = () => {
    if (!navigator.geolocation) { setStatus("denied"); return; }
    setStatus("loading");
    navigator.geolocation.getCurrentPosition(
      pos => fetchNearbyBrands(pos.coords.latitude, pos.coords.longitude),
      ()  => setStatus("denied"),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // Auto-request on mount
  useEffect(() => { requestLocation(); }, []);

  return (
    <>
      {loading && <Loader />}

      <section className={`${styles.section} ${df ? "" : styles.sectionPadding}`}>
        <div className={styles.container}>

          {/* ── Header ─────────────────────────── */}
          <div className={styles.header}>
            <div className={styles.badge}>
              {status === "granted" ? <MapPin size={14} /> : <Store size={14} />}
              <span>{status === "granted" ? "Nearby Shops" : "Our Shops"}</span>
            </div>

            <h2 className={styles.title}>
              {status === "granted" ? "Shops Near You" : "All Shops"}
            </h2>
            <p className={styles.subtitle}>
              {status === "granted"
                ? "Discover shops within 3 km of your current location"
                : "Explore our trusted and popular shops"}
            </p>
            <div className={styles.underline} />
          </div>

          {/* ── Loading ─────────────────────────── */}
          {status === "loading" && (
            <div className={styles.stateBox}>
              <div className={styles.spinner} />
              <p>Detecting your location...</p>
            </div>
          )}

          {/* ── Permission denied CTA ───────────── */}
          {status === "denied" && (
            <div className={styles.ctaBox}>
              <div className={styles.ctaIcon}>📍</div>
              <h3 className={styles.ctaTitle}>Find Shops Near You</h3>
              <p className={styles.ctaText}>
                Allow location access to see shops near your current location.
              </p>
              <button className={styles.ctaBtn} onClick={requestLocation}>
                <MapPin size={16} />
                Enable Location
              </button>
            </div>
          )}

          {/* ── Empty ───────────────────────────── */}
          {status === "empty" && (
            <div className={styles.ctaBox}>
              <div className={styles.ctaIcon}>😔</div>
              <h3 className={styles.ctaTitle}>No Shops Nearby</h3>
              <p className={styles.ctaText}>
                There are currently no shops within 3 km of your location.
              </p>
              <a href="/brand" className={styles.ctaBtn}>
                Browse All Shops
              </a>
            </div>
          )}

          {/* ── Brand Grid (Category Style) ──────── */}
          {status === "granted" && displayBrands.length > 0 && (
            <div className={styles.grid}>
              {displayBrands.map((brand, i) => (
                <a
                  key={brand?.id || i}
                  href={`/shop?brand=${brand?.id}`}
                  onClick={() => setLoading(true)}
                  className={styles.card}
                >
                  <div className={styles.imgWrap}>
                    <Image
                      src={brand?.image ?? "/noimage.png"}
                      alt={brand?.name || "brand"}
                      width={300}
                      height={300}
                      className={styles.img}
                    />
                  </div>
                  <h6 className={styles.cardName}>{brand?.name}</h6>
                  <p className={styles.distance}>
                    <MapPin size={11} style={{ marginRight: 3 }} />
                    {brand.distance} km
                  </p>
                </a>
              ))}
            </div>
          )}

          {/* ── View All ─────────────────────────── */}
          {(status === "granted" || status === "idle") && (
            <div className={styles.viewAllWrap}>
              <a href="/brand" className={styles.viewAllBtn}>
                View All Shops 
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <path d="M7 10H13M13 10L10 7M13 10L10 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
            </div>
          )}

        </div>
      </section>
    </>
  );
};

export default Brands;